/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { useTooltipInPortal, useTooltip } from "@visx/tooltip";
import { When } from "react-if";
import { BrickLoader, UniversalWrap } from "~/components";
import {
  getColorsPalette,
  StackBarChart,
  getTableColumns,
  Wrapper,
  generateLinkParameters,
  mapStackBarChartColors,
} from "~/components/Charts/CostAnalysis";
import { tooltipStyles } from "~/components/Charts/CostAnalysis/components";
import { getEndDate } from "~/components/Charts/CostAnalysis/helpers";
import {
  ChartType,
  Granularity,
  LinkQueryParameterName,
  Period,
} from "~/components/Charts/CostAnalysis/types";
import { useCurrency } from "~/hooks";
import { ApplicationCostTableData } from "../ApplicationCostTableData";
import { useApplicationEnvironmentCostChart } from "../hooks";

type Props = {
  appId: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
};

export function ApplicationCostPerEnvironmentView({
  appId,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
}: Props) {
  const { currency } = useCurrency();

  const {
    applicationTopEnvironmentsCostChartData,
    costPerEnvironmentTableData,
    applicationTopEnvironmentsCostTotal,
    loading,
    error,
    environments,
  } = useApplicationEnvironmentCostChart({
    appId,
    startDate,
    endDate: getEndDate(endDate),
    period,
    granularity,
  });

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const columns = useMemo(
    () =>
      getTableColumns({
        startDate,
        endDate,
        currency,
        data: costPerEnvironmentTableData,
        hasFooter,
        period,
        chartType: ChartType.COST_PER_ENVIRONMENT,
        colorsPalette: getColorsPalette(ChartType.COST_PER_ENVIRONMENT),
        linkParameters: generateLinkParameters(
          LinkQueryParameterName.ENVIRONMENT,
          []
        ),
      }),
    [startDate, endDate, currency, hasFooter, costPerEnvironmentTableData]
  );

  return (
    <UniversalWrap
      loaderProps={{ loading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <Wrapper onMouseLeave={hideTooltip}>
        <ParentSize>
          {(parent) => (
            <>
              <StackBarChart
                width={parent.width}
                height={280}
                data={applicationTopEnvironmentsCostChartData}
                granularity={granularity}
                loading={loading}
                startDate={startDate}
                endDate={endDate}
                period={period}
                showTooltip={showTooltip}
                hideTooltip={hideTooltip}
                colors={mapStackBarChartColors(environments)}
                keys={environments}
                containerRef={containerRef}
              />
              <When condition={tooltipOpen}>
                <TooltipInPortal
                  // set key to random so it correctly updates with parent bounds
                  key={Date.now()}
                  top={tooltipTop}
                  left={tooltipLeft}
                  style={tooltipStyles}
                >
                  {tooltipData}
                </TooltipInPortal>
              </When>
            </>
          )}
        </ParentSize>
      </Wrapper>
      <ApplicationCostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={costPerEnvironmentTableData}
        chartType={ChartType.COST_PER_ENVIRONMENT}
        total={applicationTopEnvironmentsCostTotal}
        tableError={error}
        columns={columns}
        isLoading={loading}
        showCostTable={showCostTable}
        hasFooter={hasFooter}
        linkParameters={generateLinkParameters(
          LinkQueryParameterName.ENVIRONMENT,
          []
        )}
      />
    </UniversalWrap>
  );
}
